import { Link } from '@tanstack/react-router'
import { ArrowRight, ListChecks, Search, Sparkles } from 'lucide-react'
import { parseAsInteger, parseAsString, parseAsStringLiteral, useQueryStates } from 'nuqs'
import { SearchSelect } from '@/components/search-select'
import { StatusPill } from '@/components/status-pill'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useActorName } from '@/hooks/use-actor-name'
import { useUrlSearchText } from '@/hooks/use-url-search-text'
import type { StatusTone } from '@/lib/status-tones'
import { cn, formatDateOnly, parseUTCDate, resultCount } from '@/lib/utils'
import {
  type OrderFindingRow,
  type OrderReview,
  useOrderFindings,
  useOrderReviewManufacturers,
  useOrderReviews,
  useOrderReviewWeeks,
} from '@/features/order-review/hooks'
import { SearchInput } from '@/components/search-input'
import { ListFilter, ListFilters } from '@/components/list-filters'

const LINK = 'font-medium text-primary hover:underline'

const STATUS_OPTIONS = [
  { value: 'open', label: 'To approve' },
  { value: 'applied', label: 'Applied' },
  { value: 'dismissed', label: 'Dismissed' },
  { value: 'all', label: 'All statuses' },
] as const

const RESULT_OPTIONS = [
  { value: 'all', label: 'All results' },
  { value: 'changes', label: 'With changes' },
  { value: 'matched', label: 'Matched our terms' },
  { value: 'failed', label: "Didn't finish" },
] as const

const KIND_OPTIONS = [
  { value: 'broken_case_fee', label: 'Broken-case fee' },
  { value: 'full_cases_only', label: 'Full cases only' },
  { value: 'minimum_order', label: 'Minimum order' },
  { value: 'fee', label: 'Fee not on file' },
  { value: 'dropship_fee', label: 'Dropship fee' },
  { value: 'discount', label: 'Discount' },
  { value: 'payment_terms', label: 'Payment terms' },
  { value: 'lead_time', label: 'Lead time' },
  { value: 'contact', label: 'Contact' },
  { value: 'other', label: 'Terms to review' },
]

const FINDING_STATUS: Record<string, { label: string; tone: StatusTone }> = {
  open: { label: 'To approve', tone: 'warning' },
  applied: { label: 'Applied', tone: 'success' },
  dismissed: { label: 'Dismissed', tone: 'neutral' },
}

const MAX_ORDERS = 3

function shortDay(value: string) {
  return parseUTCDate(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function FindingStatus({ row }: { row: OrderFindingRow }) {
  const actorName = useActorName()
  const status = FINDING_STATUS[row.status] ?? { label: row.status, tone: 'neutral' as const }
  return (
    <div className="space-y-1">
      <StatusPill tone={status.tone} className="whitespace-nowrap">
        {status.label}
      </StatusPill>
      <p className="text-xs text-muted-foreground">
        {row.decided_at
          ? `${actorName(row.decided_by) ?? 'Staff'} · ${shortDay(row.decided_at)}`
          : `Raised ${shortDay(row.created_at)}`}
      </p>
    </div>
  )
}

function FindingChange({ row }: { row: OrderFindingRow }) {
  return (
    <div className="min-w-0 space-y-0.5">
      <p className="text-xs font-medium text-muted-foreground">{row.kind_label}</p>
      <Link to="/order-review/$findingId" params={{ findingId: row.uuid }} className={cn(LINK, 'block leading-snug')}>
        {row.title}
      </Link>
    </div>
  )
}

function FindingTerms({ row }: { row: OrderFindingRow }) {
  if (row.kind === 'other') {
    return (
      <p className="line-clamp-3 text-sm" title={row.proposed_text}>
        {row.proposed_text}
      </p>
    )
  }
  return (
    <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm leading-5">
      <s className="text-muted-foreground decoration-muted-foreground/70">{row.on_file_text}</s>
      <ArrowRight className="size-3.5 shrink-0 text-muted-foreground" aria-label="changes to" />
      <span className="font-medium">{row.proposed_text}</span>
    </p>
  )
}

function FindingOrders({ row }: { row: OrderFindingRow }) {
  const orders = row.orders ?? []
  return (
    <div className="space-y-0.5">
      {orders.slice(0, MAX_ORDERS).map((order, index) =>
        order.po_uuid ? (
          <Link
            key={`${order.po_uuid}-${index}`}
            to="/purchase-orders/$poId"
            params={{ poId: order.po_uuid }}
            className={cn(LINK, 'block font-mono text-xs')}
          >
            {order.po_number}
          </Link>
        ) : (
          <p key={index} className="font-mono text-xs">
            {order.po_number}
          </p>
        ),
      )}
      {orders.length > MAX_ORDERS && (
        <p className="text-[11px] text-muted-foreground">+{orders.length - MAX_ORDERS} more</p>
      )}
    </div>
  )
}

function ManufacturerLink({ manufacturer }: { manufacturer: OrderFindingRow['manufacturer'] }) {
  if (!manufacturer) return null
  return (
    <Link
      to="/manufacturers/$manuId"
      params={{ manuId: manufacturer.uuid }}
      search={{ tab: 'emails' }}
      className="text-sm hover:text-primary hover:underline"
    >
      {manufacturer.name}
    </Link>
  )
}

function FindingsTable({ rows }: { rows: OrderFindingRow[] }) {
  return (
    <>
      <div className="hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Change</TableHead>
              <TableHead>Manufacturer</TableHead>
              <TableHead>Before → after</TableHead>
              <TableHead>Orders</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.uuid} className="align-top">
                <TableCell className="max-w-[320px] whitespace-normal">
                  <FindingChange row={row} />
                </TableCell>
                <TableCell className="whitespace-normal">
                  <ManufacturerLink manufacturer={row.manufacturer} />
                </TableCell>
                <TableCell className="max-w-[300px] whitespace-normal">
                  <FindingTerms row={row} />
                </TableCell>
                <TableCell>
                  <FindingOrders row={row} />
                </TableCell>
                <TableCell>
                  <FindingStatus row={row} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="space-y-2 lg:hidden">
        {rows.map((row) => (
          <div key={row.uuid} className="space-y-2 rounded-lg border bg-card p-3">
            <div className="flex items-start justify-between gap-3">
              <FindingChange row={row} />
              <FindingStatus row={row} />
            </div>
            <ManufacturerLink manufacturer={row.manufacturer} />
            <FindingTerms row={row} />
            <FindingOrders row={row} />
          </div>
        ))}
      </div>
    </>
  )
}

function ReviewResult({ review }: { review: OrderReview }) {
  const findings = review.findings ?? []
  if (findings.length > 0) {
    return (
      <ul className="space-y-0.5">
        {findings.map((finding) => (
          <li key={finding.uuid} className="flex flex-wrap items-baseline gap-x-2 text-sm">
            <Link to="/order-review/$findingId" params={{ findingId: finding.uuid }} className={LINK}>
              {finding.title}
            </Link>
            {finding.status !== 'open' && (
              <span className="text-xs text-muted-foreground">{FINDING_STATUS[finding.status]?.label}</span>
            )}
          </li>
        ))}
      </ul>
    )
  }
  if (review.status === 'queued') return <p className="text-sm text-muted-foreground">Waiting to be reviewed</p>
  return (
    <p
      className={cn('line-clamp-2 text-sm', review.status === 'failed' ? 'text-amber-800 dark:text-amber-300' : 'text-muted-foreground')}
      title={review.summary ?? undefined}
    >
      {review.status === 'failed' ? "The review didn't finish." : review.summary || 'Matched our terms'}
    </p>
  )
}

function ReviewOrder({ review }: { review: OrderReview }) {
  return (
    <div className="space-y-0.5">
      {review.po_uuid ? (
        <Link to="/purchase-orders/$poId" params={{ poId: review.po_uuid }} className={LINK}>
          {review.po_number}
        </Link>
      ) : (
        <p className="font-medium">{review.po_number}</p>
      )}
      {review.manufacturer && (
        <p className="text-xs">
          <Link
            to="/manufacturers/$manuId"
            params={{ manuId: review.manufacturer.uuid }}
            search={{ tab: 'emails' }}
            className="text-muted-foreground hover:text-primary hover:underline"
          >
            {review.manufacturer.name}
          </Link>
        </p>
      )}
    </div>
  )
}

function ReviewsTable({ rows }: { rows: OrderReview[] }) {
  return (
    <>
      <div className="hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Purchase Order</TableHead>
              <TableHead>What it showed</TableHead>
              <TableHead>Week of</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((review) => (
              <TableRow key={review.id} className="align-top">
                <TableCell className="w-56">
                  <ReviewOrder review={review} />
                </TableCell>
                <TableCell className="whitespace-normal">
                  <ReviewResult review={review} />
                </TableCell>
                <TableCell className="w-28 text-sm tabular-nums text-muted-foreground">
                  {formatDateOnly(review.week_of)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="space-y-2 lg:hidden">
        {rows.map((review) => (
          <div key={review.id} className="space-y-2 rounded-lg border bg-card p-3">
            <div className="flex items-start justify-between gap-3">
              <ReviewOrder review={review} />
              <span className="text-xs tabular-nums text-muted-foreground">{formatDateOnly(review.week_of)}</span>
            </div>
            <ReviewResult review={review} />
          </div>
        ))}
      </div>
    </>
  )
}

function TableSkeleton() {
  return (
    <div className="space-y-2" role="status" aria-label="Loading">
      {Array.from({ length: 6 }).map((_, index) => (
        <Skeleton key={index} className="h-16 w-full" />
      ))}
    </div>
  )
}

/** The weekly order review (USA-1513): the changes it proposed, and every order it reviewed. */
export function OrderReviewPage() {
  const [search, setSearch] = useUrlSearchText()
  const [filters, setFilters] = useQueryStates({
    view: parseAsStringLiteral(['changes', 'orders'] as const).withDefault('changes'),
    status: parseAsStringLiteral(['open', 'applied', 'dismissed', 'all'] as const).withDefault('open'),
    kind: parseAsString,
    result: parseAsStringLiteral(['all', 'changes', 'matched', 'failed'] as const).withDefault('all'),
    week: parseAsString,
    manufacturer: parseAsString,
    page: parseAsInteger.withDefault(1),
  })
  const { view, status, kind, result, week, manufacturer, page } = filters
  // Filters set away from their defaults for the view shown, counted on the Filters button.
  const activeFilters = [
    ...(view === 'changes' ? [status !== 'open', kind != null] : [week != null, result !== 'all']),
    manufacturer != null,
  ].filter(Boolean).length
  const manufacturers = useOrderReviewManufacturers()
  const weeks = useOrderReviewWeeks()
  const findings = useOrderFindings(
    {
      status,
      kind: kind ?? undefined,
      manufacturer_uuid: manufacturer ?? undefined,
      search: search || undefined,
      page,
      limit: 20,
    },
    view === 'changes',
  )
  const reviews = useOrderReviews(
    {
      week_of: week ?? undefined,
      result,
      manufacturer_uuid: manufacturer ?? undefined,
      search: search || undefined,
      page,
      limit: 20,
    },
    view === 'orders',
  )
  const { data, isLoading } = view === 'changes' ? findings : reviews

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold tracking-tight">Order review</h1>
        <div role="group" aria-label="Order review view" className="inline-grid grid-cols-2 rounded-lg border bg-muted/40 p-1">
          {(
            [
              { value: 'changes', label: 'Changes', Icon: Sparkles },
              { value: 'orders', label: 'Orders reviewed', Icon: ListChecks },
            ] as const
          ).map(({ value, label, Icon }) => (
            <button
              key={value}
              type="button"
              aria-pressed={view === value}
              onClick={() => void setFilters({ view: value, page: 1 })}
              className={cn(
                'inline-flex min-h-8 cursor-pointer items-center justify-center gap-2 rounded-md px-3 text-xs font-medium text-muted-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
                view === value && 'bg-background text-foreground shadow-sm',
              )}
            >
              <Icon className="size-3.5" aria-hidden="true" />
              {label}
            </button>
          ))}
        </div>
      </div>

      <ListFilters

        count={resultCount(data?.total, view === 'changes' ? 'change' : 'order')}
        active={activeFilters}
        onClear={() => void setFilters({ status: null, kind: null, result: null, week: null, manufacturer: null, page: 1 })}
        search={
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <SearchInput
              placeholder={view === 'changes' ? 'Search changes, manufacturers or POs...' : 'Search POs or manufacturers...'}
              aria-label="Search order review"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
                void setFilters({ page: 1 })
              }}
              className="pl-9"
            />
          </div>
        }
      >
        {view === 'changes' ? (
          <>
            <ListFilter label="Status">
            <Select value={status} onValueChange={(next) => void setFilters({ status: next as typeof status, page: 1 })}>
              <SelectTrigger aria-label="Status filter" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            </ListFilter>
            <ListFilter label="Change type">
            <SearchSelect
              ariaLabel="Change type filter"
              className="h-9 w-full"
              value={kind ?? undefined}
              allLabel="All types"
              options={KIND_OPTIONS}
              onChange={(next) => void setFilters({ kind: next ?? null, page: 1 })}
              searchPlaceholder="Search types"
            />
            </ListFilter>
          </>
        ) : (
          <>
            <ListFilter label="Week">
            <SearchSelect
              ariaLabel="Week filter"
              className="h-9 w-full"
              value={week ?? undefined}
              allLabel="All weeks"
              options={(weeks.data ?? []).map((value) => ({ value, label: `Week of ${formatDateOnly(value)}` }))}
              onChange={(next) => void setFilters({ week: next ?? null, page: 1 })}
              searchPlaceholder="Search weeks"
            />
            </ListFilter>
            <ListFilter label="Result">
            <Select value={result} onValueChange={(next) => void setFilters({ result: next as typeof result, page: 1 })}>
              <SelectTrigger aria-label="Result filter" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RESULT_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            </ListFilter>
          </>
        )}
        <ListFilter label="Manufacturer">
          <SearchSelect
            ariaLabel="Manufacturer filter"
            className="h-9 w-full"
            value={manufacturer ?? undefined}
            allLabel="All manufacturers"
            options={(manufacturers.data ?? []).map((row) => ({ value: row.uuid, label: row.name }))}
            onChange={(next) => void setFilters({ manufacturer: next ?? null, page: 1 })}
            searchPlaceholder="Search manufacturers"
          />
        </ListFilter>
      </ListFilters>

      {isLoading ? (
        <TableSkeleton />
      ) : !data?.items?.length ? (
        <div className="py-12 text-center text-muted-foreground">
          {view === 'changes' ? 'No changes' : 'No reviewed orders'}
        </div>
      ) : view === 'changes' ? (
        <FindingsTable rows={findings.data?.items ?? []} />
      ) : (
        <ReviewsTable rows={reviews.data?.items ?? []} />
      )}

      <div className="flex items-center justify-between px-4 py-3">
        <p className="text-sm text-muted-foreground">
          Page {data?.page ?? 1} of {Math.max(data?.pages ?? 1, 1)} ({data?.total ?? 0} total)
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => void setFilters({ page: Math.max(1, page - 1) })} disabled={page <= 1}>
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void setFilters({ page: page + 1 })}
            disabled={page >= (data?.pages ?? 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  )
}
